import React, { useEffect, useState } from 'react'
import { Box, Button, Stack, Typography, IconButton } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart'

import { useProductValidation } from '../hooks/useProductValidation'
import { useScan } from '../context/scanContext'
import AppShell from './layout/AppShell'
import VerdictScreen from './scanner/VerdictScreen'
import { Surface, InfoRow } from './ui/Surface'
import { FullScreenLoader, EmptyState } from './ui/States'
import { ProductQuantityRow } from './ProductsSelectionTable'
import { playScanResult } from '../services/soundService'

const safeName = (value) => {
  try {
    return JSON.parse(value).fr
  } catch {
    return value ?? '—'
  }
}

const ProductTicketScanResponse = ({ onCancel }) => {
  const {
    productList,
    addProductQuantity,
    removeProductQuantity,
    resetProductQuantity,
    consumeProducts,
    result,
    cannotValidate,
    validateProduct,
  } = useProductValidation()
  const { currentCode } = useScan()
  const [openResult, setOpenResult] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    setIsLoading(true)
    validateProduct(
      currentCode,
      (available) => {
        setIsLoading(false)
        // Un billet sans produit disponible est un refus : il doit sonner
        // comme tel, sans attendre que l'employé lise l'écran.
        if (!available || available.length === 0) {
          playScanResult(false)
        }
      },
      () => {
        setIsLoading(false)
        playScanResult(false)
      }
    )
  }, [])

  const handleValidation = () => {
    setIsLoading(true)
    setError(null)
    // On n'affiche le reçu qu'une fois la réponse du serveur reçue.
    consumeProducts(
      (transaction) => {
        setIsLoading(false)
        setOpenResult(true)
        playScanResult(transaction?.Result === 'Valid')
      },
      (message) => {
        setIsLoading(false)
        setError(message)
        playScanResult(false)
      }
    )
  }

  if (isLoading) {
    return <FullScreenLoader label="Traitement en cours" />
  }

  // ---- Reçu de transaction ----
  if (openResult) {
    const isValid = result?.Result === 'Valid'
    return (
      <VerdictScreen
        status={isValid ? 'valid' : 'invalid'}
        title={isValid ? 'Validé' : 'Refusé'}
        message={safeName(result?.Title)}
        footer={
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<QrCodeScannerIcon />}
            onClick={onCancel}
          >
            Billet suivant
          </Button>
        }
      >
        <Surface>
          <InfoRow label="Numéro de billet" value={result?.Number} emphasis />
        </Surface>

        <Typography variant="overline" sx={{ display: 'block', mt: 3, mb: 1, px: 0.5 }}>
          Détails de la transaction
        </Typography>
        <Surface padding={0}>
          <Box sx={{ px: 2 }}>
            {(result?.Products || [])
              .filter((item) => item.Quantity !== 0)
              .map((item, index) => {
                const consumed = productList.find((product) => product.Id === item.Id)
                return (
                  <InfoRow
                    key={item.Id ?? index}
                    label={safeName(item.ItemName)}
                    value={`× ${consumed?.QuantityToConsume ?? 0}`}
                  />
                )
              })}
          </Box>
        </Surface>
      </VerdictScreen>
    )
  }

  // ---- Aucun produit disponible sur ce billet ----
  if (productList.length === 0) {
    return (
      <VerdictScreen
        status="invalid"
        title="Aucun produit"
        message="Ce billet ne comporte aucun produit à consommer."
        footer={
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<QrCodeScannerIcon />}
            onClick={onCancel}
          >
            Billet suivant
          </Button>
        }
      >
        <EmptyState
          icon={<RemoveShoppingCartIcon />}
          title="Rien à remettre"
          description="Tous les produits de ce billet ont déjà été consommés, ou aucun produit n'y est associé."
        />
      </VerdictScreen>
    )
  }

  // ---- Sélection des produits ----
  return (
    <AppShell
      title="Choisissez les produits"
      subtitle={`${productList.length} produit${productList.length > 1 ? 's' : ''} disponible${
        productList.length > 1 ? 's' : ''
      }`}
      leading={
        <IconButton onClick={onCancel} aria-label="Annuler" edge="start">
          <ArrowBackIcon />
        </IconButton>
      }
      footer={
        <Stack spacing={1}>
          {error ? (
            <Typography variant="body2" color="error" textAlign="center">
              {error}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1.5}>
            <Button fullWidth variant="outlined" color="inherit" size="large" onClick={onCancel}>
              Annuler
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="large"
              disabled={cannotValidate}
              onClick={handleValidation}
            >
              Valider
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Surface padding={0} sx={{ overflow: 'hidden' }}>
        {productList.map((row) => (
          <ProductQuantityRow
            key={row.Id}
            product={row}
            onAdd={addProductQuantity}
            onRemove={removeProductQuantity}
          />
        ))}
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button color="error" size="small" onClick={() => resetProductQuantity()}>
            Tout retirer
          </Button>
        </Box>
      </Surface>
    </AppShell>
  )
}

export default ProductTicketScanResponse
